import { ChevronLeft, ChevronRight, Folder as FolderIcon, FolderPlus, Layers, Star } from "lucide-react";
import { useState } from "react";
import { ActionMenu } from "../../components/ui/action-menu";
import { useToast } from "../../components/ui/toast";
import { usePersistentState } from "../../hooks/use-persistent-state";
import { PromptModal } from "../../components/ui/prompt-modal";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { cn } from "../../lib/utils";
import { useFolders, useFolderCounts, useFolderMutations } from "./queries";
import type { Folder } from "./types";

export function FolderSidebar({ active, onSelect }: { active: string | null; onSelect: (id: string | null) => void }) {
  const { data: folders = [] } = useFolders();
  const count = useFolderCounts();
  const m = useFolderMutations();
  const toast = useToast();
  const [collapsed, setCollapsed] = usePersistentState("home:folders-collapsed", false);
  const [dialog, setDialog] = useState<
    | { kind: "create" }
    | { kind: "rename"; id: string; name: string }
    | { kind: "delete"; id: string; name: string }
    | null
  >(null);

  if (collapsed) {
    return (
      <div className="flex h-fit shrink-0 flex-col items-center gap-1 rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-2">
        <button title="All Agents" onClick={() => onSelect(null)} className={cn("rounded-lg p-2", active === null ? "bg-accent-50 text-accent-700 dark:bg-accent-500/10 dark:text-accent-400" : "text-ink-faint hover:bg-soft hover:text-ink")}><Layers className="h-4 w-4" /></button>
        {folders.map((f) => (
          <button key={f.folder_id} title={`${f.name} (${count(f.folder_id)})`} onClick={() => onSelect(f.folder_id)}
            className={cn("relative rounded-lg p-2", active === f.folder_id ? "bg-accent-50 text-accent-700 dark:bg-accent-500/10 dark:text-accent-400" : "text-ink-faint hover:bg-soft hover:text-ink")}>
            <FolderIcon className="h-4 w-4" />
          </button>
        ))}
        <button title="Expand" onClick={() => setCollapsed(false)} className="mt-1 rounded-lg p-2 text-ink-faint hover:bg-soft hover:text-ink"><ChevronRight className="h-4 w-4" /></button>
      </div>
    );
  }

  return (
    <div className="h-fit w-full shrink-0 rounded-lg shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-2.5 lg:w-56">
      <div className="flex items-center justify-between px-1.5 pb-2">
        <p className="font-mono text-[9px] font-semibold uppercase tracking-wider text-ink-faint">Folders</p>
        <button aria-label="Collapse folders" onClick={() => setCollapsed(true)} className="rounded p-0.5 text-ink-faint hover:bg-soft hover:text-ink"><ChevronLeft className="h-3 w-3" /></button>
      </div>

      <Row active={active === null} onClick={() => onSelect(null)} icon={<Layers className="h-3.5 w-3.5" />} label="All Agents" count={count(null)} />

      <div className="my-1.5 border-t border-line" />

      {folders.map((f: Folder) => (
        <div key={f.folder_id} className="group flex items-center rounded-md transition-colors">
          <Row active={active === f.folder_id} onClick={() => onSelect(f.folder_id)} icon={<FolderIcon className="h-3.5 w-3.5" />} label={f.name} count={count(f.folder_id)} />
          <ActionMenu ariaLabel={`Folder ${f.name}`} items={[
            { label: "Rename", onClick: () => setDialog({ kind: "rename", id: f.folder_id, name: f.name }) },
            { label: "Delete", danger: true, onClick: () => setDialog({ kind: "delete", id: f.folder_id, name: f.name }) },
          ]} />
        </div>
      ))}

      <button
        onClick={() => setDialog({ kind: "create" })}
        className="mt-1.5 flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-ink shadow-[0_0_0_1px_rgba(28,25,23,0.08)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08)] dark:text-stone-100 hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-500/10 dark:hover:text-accent-400 transition-colors"
      >
        <FolderPlus className="h-3.5 w-3.5" /> New Folder
      </button>

      {dialog?.kind === "create" && (
        <PromptModal
          title="New Folder" label="Folder name" placeholder="e.g., Healthcare" submitLabel="Create"
          onClose={() => setDialog(null)}
          onSubmit={(name) => { m.create.mutate(name, { onSuccess: () => toast.success("Folder created", name) }); setDialog(null); }}
        />
      )}
      {dialog?.kind === "rename" && (
        <PromptModal
          title="Rename Folder" label="Folder name" initial={dialog.name} submitLabel="Save"
          onClose={() => setDialog(null)}
          onSubmit={(name) => { m.rename.mutate({ id: dialog.id, name }, { onSuccess: () => toast.success("Folder renamed", name) }); setDialog(null); }}
        />
      )}
      {dialog?.kind === "delete" && (
        <ConfirmModal
          title="Delete folder?"
          body={`"${dialog.name}" will be deleted. Its agents move to All Agents. This cannot be undone.`}
          confirmLabel="Delete"
          onClose={() => setDialog(null)}
          onConfirm={() => { m.remove.mutate(dialog.id, { onSuccess: () => toast.danger("Folder deleted", dialog.name) }); setDialog(null); }}
        />
      )}
    </div>
  );
}

function Row({ active, onClick, icon, label, count }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; count: number }) {
  return (
    <button onClick={onClick} className={cn("flex flex-1 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs transition-colors", active ? "bg-accent-50 font-semibold text-accent-700 dark:bg-accent-500/10 dark:text-accent-400" : "text-ink-muted hover:bg-soft hover:text-ink")}>
      {icon} <span className="flex-1 truncate text-left">{label}</span>
      <span className="rounded-full bg-soft px-1.5 py-0.5 font-mono text-[9px] text-ink-faint">{count}</span>
    </button>
  );
}
